/**
 * Suburb map checks and grow, as pure functions over the adjacency graph (#256; ported from the standalone mapper's
 * runLint / growRegion in conductor-suburb-mapper/public/app.js, so both report the same findings).
 *
 * The graph is assets/suburbs/graph.json: nodes[salCode] = [name, stateAbbrev, lng, lat] (an interior point) and
 * edges[salCode] = neighbours (polygons sharing an edge; 42,167 edges, 457 across a state line).
 */

export interface SuburbGraph {
  nodes: Record<string, [string, string, number, number]>;
  edges: Record<string, string[]>;
}

export type Finding =
  | { kind: 'enclave'; code: string; regions: string[]; fix: string | null }
  | { kind: 'island' | 'offshore'; code: string; region: string; size: number; codes: string[] }
  | { kind: 'cross-state'; code: string; region: string; state: string };

export interface CrossStateGroup {
  region: string;
  state: string;
  codes: string[];
}

/**
 * Three checks over every assignment, in every state:
 *   enclave      an unassigned suburb whose neighbours are all assigned: almost always a miss. Fixable in one step
 *                when the surrounding region is unanimous.
 *   island       a region in disconnected pieces that border other land: usually a stray click. A piece with no land
 *                border at all (Bribie, Stradbroke, the bay islands) is reported as offshore, for information.
 *   cross-state  a suburb in a region whose declared state differs. Legitimate near a border (the Northern Rivers
 *                under Brisbane), suspicious elsewhere; grouped by region and state when shown.
 */
export function runChecks(
  graph: SuburbGraph,
  regionOf: Map<string, string>,
  regionState: Map<string, string | null>
): Finding[] {
  const { nodes, edges } = graph;
  const findings: Finding[] = [];

  for (const code of Object.keys(nodes)) {
    if (regionOf.has(code)) continue;
    const nbrs = edges[code];
    if (!nbrs || !nbrs.length) continue;
    const around = new Set<string>();
    let all = true;
    for (const nb of nbrs) {
      const r = regionOf.get(nb);
      if (!r) {
        all = false;
        break;
      }
      around.add(r);
    }
    if (all)
      findings.push({
        kind: 'enclave',
        code,
        regions: [...around],
        fix: around.size === 1 ? [...around][0] : null,
      });
  }

  const byRegion = new Map<string, string[]>();
  for (const [code, r] of regionOf) (byRegion.get(r) ?? byRegion.set(r, []).get(r)!).push(code);
  for (const [r, codes] of byRegion) {
    const member = new Set(codes);
    const seen = new Set<string>();
    const comps: string[][] = [];
    for (const start of codes) {
      if (seen.has(start)) continue;
      const comp: string[] = [];
      const stack = [start];
      seen.add(start);
      while (stack.length) {
        const c = stack.pop()!;
        comp.push(c);
        for (const nb of edges[c] ?? [])
          if (member.has(nb) && !seen.has(nb)) {
            seen.add(nb);
            stack.push(nb);
          }
      }
      comps.push(comp);
    }
    if (comps.length < 2) continue;
    comps.sort((a, b) => b.length - a.length);
    for (const comp of comps.slice(1)) {
      const inComp = new Set(comp);
      const landlocked = comp.every((c) => (edges[c] ?? []).every((nb) => inComp.has(nb)));
      findings.push({
        kind: landlocked ? 'offshore' : 'island',
        code: comp[0],
        region: r,
        size: comp.length,
        codes: comp,
      });
    }
  }

  for (const [code, r] of regionOf) {
    const want = regionState.get(r);
    const have = nodes[code]?.[1];
    if (want && have && want !== have)
      findings.push({ kind: 'cross-state', code, region: r, state: have });
  }
  return findings;
}

/** Cross-state findings grouped by region and state, largest first, so 400 legitimate rows collapse to one line. */
export function groupCrossState(findings: Finding[]): CrossStateGroup[] {
  const groups = new Map<string, CrossStateGroup>();
  for (const f of findings) {
    if (f.kind !== 'cross-state') continue;
    const k = `${f.region}>${f.state}`;
    (
      groups.get(k) ?? groups.set(k, { region: f.region, state: f.state, codes: [] }).get(k)!
    ).codes.push(f.code);
  }
  return [...groups.values()].sort((a, b) => b.codes.length - a.codes.length);
}

/**
 * Grow: every unassigned suburb that borders the region, limited to `allowed` (the suburbs drawn in the loaded
 * state), so growth never spills into a state you aren't looking at.
 */
export function growFrontier(
  graph: SuburbGraph,
  regionOf: Map<string, string>,
  region: string,
  allowed: Set<string>
): string[] {
  const frontier = new Set<string>();
  for (const [code, r] of regionOf) {
    if (r !== region) continue;
    for (const nb of graph.edges[code] ?? [])
      if (!regionOf.has(nb) && allowed.has(nb)) frontier.add(nb);
  }
  return [...frontier];
}
